import Decimal from 'decimal.js';

import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { Callout } from '@/components/notifications/Callout/Callout';
import { DispositiveProvisionDispositionKind } from '@/types/schema';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { formatPercentSmart } from '@/utils/formatting/percent';

/**
 * Maps formula disposition kinds to their display names for the callout.
 * Uses simplified names without parenthetical dollar amounts.
 */
const FORMULA_DISPLAY_NAMES: Partial<
  Record<DispositiveProvisionDispositionKind, string>
> = {
  [DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor]:
    'remaining federal lifetime exemption',
  [DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion]:
    'remaining GST exemption in excess of federal lifetime exemption',
  [DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantor]:
    'remaining GST exemption',
  [DispositiveProvisionDispositionKind.RemainingStateExemption]:
    'remaining state exemption',
  [DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption]:
    'remaining federal exemption in excess of state exemption',
  [DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption]:
    'lesser of remaining state or federal exemption',
  [DispositiveProvisionDispositionKind.AnythingLeftOver]: 'anything left over',
};

interface DistributionSummaryCalloutProps {
  distributionKind: 'amount' | 'percentage' | 'formula';
  dispositionAmount: Decimal | null | undefined;
  dispositionPercentage: Decimal | null | undefined;
  dispositionFormulaicPercentage: Decimal | null | undefined;
  dispositionLimit: Decimal | null | undefined;
  formulaKind: DispositiveProvisionDispositionKind | '' | undefined;
  bearsTax: boolean | undefined;
  growByInflation: boolean | undefined;
}

export function DistributionSummaryCallout({
  distributionKind,
  dispositionAmount,
  dispositionPercentage,
  dispositionFormulaicPercentage,
  dispositionLimit,
  formulaKind,
  bearsTax,
  growByInflation,
}: DistributionSummaryCalloutProps) {
  // dispositionLimit is only meaningful for percentage distributions; ignore
  // stale values that may linger in form state after switching distribution kind.
  const hasLimit =
    distributionKind === 'percentage' &&
    dispositionLimit != null &&
    !dispositionLimit.isZero();
  const hasFormulaPercentage =
    distributionKind === 'formula' && dispositionFormulaicPercentage != null;

  const prefix = getPrefix({
    distributionKind,
    dispositionAmount,
    dispositionPercentage,
    dispositionFormulaicPercentage,
    formulaKind,
    hasFormulaPercentage,
  });

  // If we can't construct a meaningful prefix, don't show the callout
  if (!prefix) return null;

  const parts: string[] = [`${prefix} will distribute to this recipient`];

  if (hasLimit) {
    parts.push(`, limited to ${formatCurrencyNoDecimals(dispositionLimit)}`);
  }

  if (bearsTax) {
    parts.push(', less any taxes');
  }

  // Close the first sentence
  parts.push('.');

  // "Distribution grows with inflation" only applies when there is a concrete
  // dollar value that can grow. Suppressed for:
  //   - Percentage distributions without a limit (nothing to grow)
  //   - ALO formula distributions (no discrete dollar value)
  // Exemption-based formula kinds (federal, GST, state, etc.) DO have concrete
  // dollar values that grow with inflation, so we keep the copy for those.
  const isAnythingLeftOver =
    distributionKind === 'formula' &&
    formulaKind === DispositiveProvisionDispositionKind.AnythingLeftOver;
  const showInflationCopy =
    growByInflation &&
    !isAnythingLeftOver &&
    !(distributionKind === 'percentage' && !hasLimit);

  if (showInflationCopy) {
    parts.push(' Distribution grows with inflation.');
  }

  return (
    <FormLayoutRow>
      <FormLayoutItem width={12}>
        <Callout severity="info-high">{parts.join('')}</Callout>
      </FormLayoutItem>
    </FormLayoutRow>
  );
}

function getPrefix({
  distributionKind,
  dispositionAmount,
  dispositionPercentage,
  dispositionFormulaicPercentage,
  formulaKind,
  hasFormulaPercentage,
}: {
  distributionKind: 'amount' | 'percentage' | 'formula';
  dispositionAmount: Decimal | null | undefined;
  dispositionPercentage: Decimal | null | undefined;
  dispositionFormulaicPercentage: Decimal | null | undefined;
  formulaKind: DispositiveProvisionDispositionKind | '' | undefined;
  hasFormulaPercentage: boolean;
}): string | null {
  if (distributionKind === 'amount') {
    if (!dispositionAmount) return null;
    return formatCurrencyNoDecimals(dispositionAmount);
  }

  if (distributionKind === 'percentage') {
    if (!dispositionPercentage) return null;
    return `${formatPercentSmart(dispositionPercentage)}%`;
  }

  // Formula distribution
  if (!formulaKind) return null;

  const formulaDisplayName =
    FORMULA_DISPLAY_NAMES[formulaKind as DispositiveProvisionDispositionKind] ??
    'the selected formula';

  if (hasFormulaPercentage && dispositionFormulaicPercentage) {
    return `${formatPercentSmart(dispositionFormulaicPercentage)}% of ${formulaDisplayName}`;
  }

  // Capitalize first letter for standalone formula name
  return (
    formulaDisplayName.charAt(0).toUpperCase() + formulaDisplayName.slice(1)
  );
}
